import { Fragment, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useDispatch, useSelector } from 'react-redux';
import { FormControlLabel, RadioGroup, makeStyles, Tooltip } from '@material-ui/core';
import { FaInfoCircle } from 'react-icons/fa';
import Dropdown from '../../../../Dropdown';
import Filter from '../../../../Filters/Filter';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import Checkbox from '../../../../Checkbox';
import {
  FiltersInitialValue,
  FlightCabinsFilterValues,
  FlightSearchTypes,
  NonStandardCabins,
} from '../../../../../constants';
import TwRadio from '../../../../TwRadio';

const useStyles = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.black,
  },
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.black,
    fontSize: '14px',
  },
}));
const useStylesRadio = makeStyles(() => ({
  root: {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'var(--fontFamilyBody)',
    },
  },
}));
const CabinClassFilter = ({ filterMaxWidthClass, tripId, filterBgColor }) => {
  const classes = useStyles();
  const classesRadio = useStylesRadio();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const { travelAgency } = useSelector((store) => store.user);
  const isEmptyFilter = useMemo(
    () =>
      searchInputs.keepSameCabin === FiltersInitialValue.flight.keepSameCabin &&
      searchInputs.avoidCabinDowngrade === FiltersInitialValue.flight.avoidCabinDowngrade &&
      !_.find(searchInputs.flightSegments, (fs) => fs.preferredCabin),
    [searchInputs.flightSegments, searchInputs.keepSameCabin, searchInputs.avoidCabinDowngrade],
  );

  /**
   * If any flight segment has a cabin class selected (other than Any)
   * then show 'Avoid Cabin Downgrade' filter, Otherwise hide it.
   */
  const showAvoidCabinDowngrade = useMemo(
    () =>
      !!(
        Object.keys(searchInputs).length !== 0 &&
        searchInputs &&
        searchInputs.flightSegments &&
        searchInputs.flightSegments.length > 0 &&
        searchInputs.flightSegments.find((x) => x.preferredCabin)
      ),
    [searchInputs],
  );
  const doHideNonStandardCabins = !!travelAgency?.settings?.doHideNonStandardCabins;
  const filteredCabinKeys = useMemo(
    () =>
      Object.keys(FlightCabinsFilterValues).filter(
        (key) => !doHideNonStandardCabins || !NonStandardCabins.includes(key),
      ),
    [doHideNonStandardCabins],
  );

  const getLabel = useMemo(() => {
    if (isEmptyFilter) return 'Cabin Class';

    let result = '';
    if (searchInputs.keepSameCabin !== FiltersInitialValue.flight.keepSameCabin) {
      result += ', Keep Same Cabin';
    }
    if (searchInputs.avoidCabinDowngrade !== FiltersInitialValue.flight.avoidCabinDowngrade) {
      result += ', Avoid Cabin Downgrade';
    }
    if (
      !_.find(searchInputs.flightSegments, (fs) => fs.preferredCabin) &&
      (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY ||
        !searchInputs.hasDifferentOutboundInboundCabins)
    ) {
      if (result) return result.substring(1); // remove the comma.
      if (!result) return 'Cabin Class';
    }

    if (
      searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY ||
      !searchInputs.hasDifferentOutboundInboundCabins
    ) {
      return `${FlightCabinsFilterValues[searchInputs.flightSegments[0].preferredCabin || '']} Cabin${result}`;
    }
    if (searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP) {
      return `Outbound: ${FlightCabinsFilterValues[searchInputs.flightSegments[0].preferredCabin || '']}, Inbound: ${FlightCabinsFilterValues[searchInputs.flightSegments[1].preferredCabin || '']}${result}`;
    }
    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
      return `${FlightCabinsFilterValues[searchInputs.flightSegments[0].preferredCabin || '']} Cabin ${result}`;
    }

    const labels = searchInputs.flightSegments.map((segment, index) => {
      const flightNumber = index + 1;
      const cabin = FlightCabinsFilterValues[segment.preferredCabin || ''];
      return `Flight ${flightNumber}: ${cabin}`;
    });
    labels.push(result.substring(1));
    return labels.join(', ');
  }, [
    isEmptyFilter,
    searchInputs.avoidCabinDowngrade,
    searchInputs.flightSearchType,
    searchInputs.flightSegments,
    searchInputs.hasDifferentOutboundInboundCabins,
    searchInputs.keepSameCabin,
  ]);

  const handleChange = useCallback(
    (newValue) => {
      const payload = {
        tripId,
        searchObj: {
          flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
        },
      };

      if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
        payload.searchObj.flightSegments[0] = {
          ...payload.searchObj.flightSegments[0],
          ..._.omit(newValue, 'rowIndex'),
        };
      } else if (
        searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY ||
        searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
      ) {
        if (searchInputs.hasDifferentOutboundInboundCabins) {
          payload.searchObj.flightSegments[newValue.rowIndex] = {
            ...payload.searchObj.flightSegments[newValue.rowIndex],
            ..._.omit(newValue, 'rowIndex'),
          };
        } else {
          searchInputs.flightSegments.forEach((segment, index) => {
            payload.searchObj.flightSegments[index] = {
              ...payload.searchObj.flightSegments[index],
              ..._.omit(newValue, 'rowIndex'),
            };
          });
        }
      }

      // Use flight segments latest values, NOT from searchInputs due to async nature.
      const resetAvoidCabinDowngrade = payload.searchObj.flightSegments.every(
        (x) => !x.preferredCabin,
      );
      if (resetAvoidCabinDowngrade) {
        payload.searchObj.avoidCabinDowngrade = FiltersInitialValue.flight.avoidCabinDowngrade;
      } else {
        const defaultDoAvoidCabinDowngrade =
          travelAgency?.filterSettings?.defaultDoAvoidCabinDowngrade;
        if (defaultDoAvoidCabinDowngrade && searchInputs.avoidCabinDowngrade === false) {
          payload.searchObj.avoidCabinDowngrade = true;
        }
      }

      if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
      else dispatch(updateFlightSearchObj(payload));
      track('cabinClassFilter.handleChange', {
        ...newValue,
      });
    },
    [
      dispatch,
      isTripSearch,
      track,
      tripId,
      searchInputs.flightSegments,
      searchInputs.flightSearchType,
      searchInputs.hasDifferentOutboundInboundCabins,
      searchInputs.avoidCabinDowngrade,
      travelAgency,
    ],
  );

  const handleHasDifferentCabin = useCallback(
    (newValue) => {
      const payload = {
        tripId,
        searchObj: {
          ...newValue,
          flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
        },
      };

      payload.searchObj.flightSegments.forEach((segment, index) => {
        payload.searchObj.flightSegments[index] = {
          ...payload.searchObj.flightSegments[index],
          preferredCabin: '',
        };
      });

      // When we toggle 'Different outbound/inbound classes' OR 'Different classes for all flights'
      // Cabins gets reset to 'Any', Consequently, Reset the value of AvoidCabinDowngrade.
      payload.searchObj.avoidCabinDowngrade = FiltersInitialValue.flight.avoidCabinDowngrade;

      if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
      else dispatch(updateFlightSearchObj(payload));
      track('CabinClassFilter.handleHasDifferentCabin', {
        ...newValue,
      });
    },
    [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId],
  );

  const handleClearFilter = useCallback(() => {
    handleHasDifferentCabin({
      hasDifferentOutboundInboundCabins: false,
      avoidCabinDowngrade: FiltersInitialValue.flight.avoidCabinDowngrade,
      keepSameCabin: FiltersInitialValue.flight.keepSameCabin,
    });

    track('filterBar.clearFilter');
  }, [handleHasDifferentCabin, track]);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    const payload = {
      tripId,
      searchObj: {
        [name]: checked,
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
  };

  const CabinClassOptions = (properyName, flightSegmetRowIndex = 0) => (
    <RadioGroup
      className="text-theme"
      classes={classesRadio}
      value={
        (searchInputs.flightSegments &&
          searchInputs.flightSegments[flightSegmetRowIndex] &&
          searchInputs.flightSegments[flightSegmetRowIndex][properyName]) ||
        ''
      }
      onChange={(event, newValue) =>
        handleChange({
          [properyName]: newValue,
          rowIndex: flightSegmetRowIndex,
        })
      }
    >
      {filteredCabinKeys.map((key) => (
        <FormControlLabel
          key={key}
          value={key}
          control={<TwRadio />}
          label={FlightCabinsFilterValues[key]}
        />
      ))}
    </RadioGroup>
  );

  return (
    <Filter
      filterMaxWidthClass={filterMaxWidthClass}
      label={getLabel}
      menuWidthClass="w-max"
      widthInside="w-95"
      isFilterEmpty={isEmptyFilter}
      handleClear={handleClearFilter}
      disableCloseOnOutsideClick
      variant={isTripSearch ? undefined : 'outline'}
      bgClassName={!isTripSearch ? '' : filterBgColor}
    >
      <Dropdown.Item withGridLayout={false}>
        {showAvoidCabinDowngrade && (
          <>
            <Checkbox
              label="Avoid cabin downgrade"
              checked={searchInputs.avoidCabinDowngrade}
              name="avoidCabinDowngrade"
              onChange={handleCheckboxChange}
            />
            <Tooltip
              arrow
              classes={classes}
              title="Guarantees that lower cabin class won't be applied on the entire itinerary"
              placement="top"
              enterTouchDelay={0}
            >
              <span>
                <FaInfoCircle className="inline text-base cursor-pointer" />
              </span>
            </Tooltip>
            <br />
          </>
        )}

        <Checkbox
          label="Keep the same cabin"
          checked={searchInputs.keepSameCabin}
          name="keepSameCabin"
          onChange={handleCheckboxChange}
        />
        <Tooltip
          arrow
          classes={classes}
          title="Guarantees that all segments belong to the requested cabin"
          placement="top"
          enterTouchDelay={0}
        >
          <span>
            <FaInfoCircle className="inline text-base cursor-pointer" />
          </span>
        </Tooltip>
        <br />

        {searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY ? (
          <Checkbox
            checked={!!searchInputs.hasDifferentOutboundInboundCabins}
            onChange={(event, newValue) =>
              handleHasDifferentCabin({
                hasDifferentOutboundInboundCabins: newValue,
              })
            }
            label={
              searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
                ? 'Different outbound/inbound classes'
                : 'Different classes for all flights'
            }
          />
        ) : (
          <></>
        )}
        {searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY &&
          CabinClassOptions('preferredCabin')}
        {!searchInputs.hasDifferentOutboundInboundCabins &&
          searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY &&
          CabinClassOptions('preferredCabin')}

        {searchInputs.hasDifferentOutboundInboundCabins &&
        searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP ? (
          <>
            <div className="mt-2">Outbound:</div>
            {CabinClassOptions('preferredCabin', 0)}
            <div className="mt-2">Inbound:</div>
            {CabinClassOptions('preferredCabin', 1)}
          </>
        ) : (
          <></>
        )}

        {searchInputs.hasDifferentOutboundInboundCabins &&
          searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY &&
          searchInputs.flightSegments.map((flightSegment, currentIndex) => (
            <>
              <div key={`${currentIndex.toString()}`} className="mt-2">
                Flight
                <span className="mx-1">{currentIndex + 1}:</span>
              </div>
              {CabinClassOptions('preferredCabin', currentIndex)}
            </>
          ))}
      </Dropdown.Item>
    </Filter>
  );
};

CabinClassFilter.propTypes = {
  tripId: PropTypes.string,
  filterMaxWidthClass: PropTypes.string,
  filterBgColor: PropTypes.string,
};

CabinClassFilter.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
  filterMaxWidthClass: 'max-w-full',
};

export default memo(CabinClassFilter);
